<template>
  <div
    data-type="textarea"
    class="textarea"
    :class="{
      greenMode,
      composite,
    }"
    @click="onCopy"
  >
    <textarea
      :id="inputId"
      :class="{ invalid: $v.$error }"
      :style="computedStyles"
      :rows="rows"
      :readonly="readonly"
      :title="inputTitle"
      :placeholder="inputPlaceholder"
      :maxlength="maxLength"
      :value="internalValue"
      @input="onInput"
      @focus="onResize"
      @keydown="keyDownEventHadler"
      @click.stop="$emit('click')"
    >
    </textarea>

    <label :for="inputId">
      <span> {{ inputName }} </span>
      <span v-if="showCounter">
        {{ counter }}
      </span>
      <span v-if="$v.$error" class="validation" :title="getValidationInfo($v)"><mark></mark></span>
      <a v-if="capustaInfo" class="hint" @click="$showInfo(capustaInfo)"> </a>
      <a v-if="greenMode" class="copy copy-fullsize" @click="copy">
        {{ $t('copy.link') }}
      </a>
    </label>

    <slot name="link"></slot>
  </div>
</template>

<script>
import { requiredIf, minLength, maxLength } from 'vuelidate/lib/validators';
import { getValidationInfo } from '@/services/inputMixins';

export default {
  name: 'InputTextarea',
  mixins: [
    getValidationInfo,
  ],
  props: {
    value: [Number, String],
    inputName: String,
    inputTitle: String,
    inputPlaceholder: String,
    inputId: String,
    capustaInfo: String,
    readonly: Boolean,
    minLength: {
      type: Number,
      default: () => 0,
    },
    maxLength: {
      type: Number,
      default: () => 255,
    },
    rows: {
      type: Number,
      default: () => 1,
    },
    required: {
      type: Boolean,
      default: () => true,
    },
    showCounter: {
      type: Boolean,
      default: () => true,
    },
    greenMode: {
      type: Boolean,
      default: () => false,
    },
    maxHeight: {
      type: Number,
      default: () => 200,
    },
    composite: {
      type: Boolean,
      default: () => false,
    },
  },
  mounted() {
    this.onResize();
  },
  data() {
    return {
      height: 'auto',
      alertShowed: false,
      maxHeightScroll: false,
      internalValue: '',
    };
  },
  validations() {
    return {
      value: {
        required: requiredIf(() => this.required),
        minLength: minLength(this.minLength),
        maxLength: maxLength(this.maxLength),
      },
    };
  },
  computed: {
    computedStyles() {
      return {
        height: this.height,
        overflow: this.maxHeightScroll ? 'auto' : 'hidden',
      };
    },
    counter() {
      return this.getCounter(this.maxLength, this.internalValue);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.internalValue = val;
        this.$nextTick(this.onResize);
      },
    },
    internalValue: {
      immediate: true,
      handler(val) {
        if (!val) {
          return;
        }

        this.$emit('input', val.trim());
        this.$v.$touch();
      },
    },
  },
  methods: {
    onCopy() {
      if (!this.greenMode) {
        return;
      }

      this.copy();
    },
    getCounter(maxLength, value) {
      if (!value) {
        return this.maxLength;
      }

      return maxLength - String(value).length;
    },
    onInput(e) {
      this.$nextTick(this.onResize);

      this.internalValue = e.target.value;
      this.$emit('input', e.target.value.trim());
    },
    onResize() {
      this.height = 'auto';

      this.$nextTick(() => {
        const input = this.$el.querySelector('textarea');
        let contentHeight = input && input.scrollHeight + 1;

        if (this.maxHeight) {
          if (contentHeight > this.maxHeight) {
            contentHeight = this.maxHeight;
            this.maxHeightScroll = true;
          } else {
            this.maxHeightScroll = false;
          }
        }

        this.height = `${contentHeight}px`;
      });

      return this;
    },
    alert() {
      if (!this.alertShowed) {
        this.alertShowed = true;

        this.$alert({
          type: 'warning',
          text: 'too.long.text',
        });

        setTimeout(() => {
          this.alertShowed = false;
        }, 3000);
      }
    },
    keyDownEventHadler(e) {
      const mapKey = {
        27: true,
        37: true,
        38: true,
        39: true,
        40: true,
        46: true,
        8: true,
      };

      const valueLength = this.internalValue && String(this.internalValue).length;

      if (valueLength >= this.maxLength) {
        if (!mapKey[e.keyCode]) {
          this.alert();
          e.preventDefault();
          return;
        }
      }
    },
    copy() {
      this.$copyText(this.internalValue);
      this.$alert({
        type: 'ok',
        text: 'link.copied',
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  textarea
    resize: none

  .description-textarea

    @media (max-width: 480px)
      textarea
        padding: 0

      > label
        > span
          &:first-of-type
            color: #989F95

        &::before
          background-color: #3F4C3A

        &::after
          background-color: #313F2B

      textarea
        color: #FFFFFF

        &:focus

          ~ label
            &::before
              background-color: #3F4C3A

            &::after
              background-color: #313F2B

    .copy-fullsize
      +full-size
      margin: 0
      width: 100%

      &::before
        top: .5rem
        right: .5rem
        width: 1.5rem
        height: 1.5rem
</style>
